import React, { useMemo } from 'react';
import './TeamEventComparison.css';

const TeamEventComparison = ({ filteredEvents, homeTeamName, awayTeamName, EVENT_TYPES }) => {
    // Group events by type and team
    const eventsByTeam = useMemo(() => {
        const result = {
            eventTypes: [],
            home: {},
            away: {},
            totals: {}
        };

        // Initialize counters for each event type
        EVENT_TYPES.forEach(type => {
            result.home[type] = 0;
            result.away[type] = 0;
            result.totals[type] = 0;
        });

        // Count events by type and team
        filteredEvents.forEach(event => {
            if (event.team === 'Home') {
                result.home[event.eventType]++;
            } else {
                result.away[event.eventType]++;
            }
            result.totals[event.eventType]++;
        });

        // Filter to only include event types that appear in the filtered events
        result.eventTypes = EVENT_TYPES.filter(type => result.totals[type] > 0)
            .sort((a, b) => result.totals[b] - result.totals[a]);

        return result;
    }, [filteredEvents, EVENT_TYPES]);

    // Calculate team totals
    const teamTotals = useMemo(() => {
        const home = Object.values(eventsByTeam.home).reduce((sum, count) => sum + count, 0);
        const away = Object.values(eventsByTeam.away).reduce((sum, count) => sum + count, 0);
        const total = home + away;
        
        return {
            home,
            away,
            total,
            homePercentage: total > 0 ? Math.round((home / total) * 100) : 0,
            awayPercentage: total > 0 ? Math.round((away / total) * 100) : 0
        };
    }, [eventsByTeam]);

    return (
        <div className="stats-section">
            <h3 className="stats-section-title">Team Event Comparison</h3>
            
            {filteredEvents.length === 0 ? (
                <div className="no-data-message">
                    <p>No events match the current filters.</p>
                </div>
            ) : (
                <>
                    <div className="team-comparison-summary">
                        <div className="team-comparison-total">
                            <div className="team-label home-team">{homeTeamName}</div>
                            <div className="team-count">{teamTotals.home} events ({teamTotals.homePercentage}%)</div>
                        </div>
                        <div className="team-comparison-bar">
                            <div 
                                className="home-bar" 
                                style={{ width: `${teamTotals.homePercentage}%` }}
                            ></div>
                            <div 
                                className="away-bar" 
                                style={{ width: `${teamTotals.awayPercentage}%` }}
                            ></div>
                        </div>
                        <div className="team-comparison-total">
                            <div className="team-label away-team">{awayTeamName}</div>
                            <div className="team-count">{teamTotals.away} events ({teamTotals.awayPercentage}%)</div>
                        </div>
                    </div>

                    <div className="team-event-table-container">
                        <table className="team-event-table">
                            <thead>
                                <tr>
                                    <th>Event Type</th>
                                    <th>{homeTeamName}</th>
                                    <th>Distribution</th>
                                    <th>{awayTeamName}</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {eventsByTeam.eventTypes.map(type => {
                                    const homeCount = eventsByTeam.home[type];
                                    const awayCount = eventsByTeam.away[type];
                                    const totalCount = homeCount + awayCount;
                                    const homePercentage = totalCount > 0 ? Math.round((homeCount / totalCount) * 100) : 0;
                                    const awayPercentage = totalCount > 0 ? Math.round((awayCount / totalCount) * 100) : 0;
                                    
                                    return (
                                        <tr key={type}>
                                            <td>{type}</td>
                                            <td className="count-cell">
                                                {homeCount} 
                                                <span className="percentage">({homePercentage}%)</span>
                                            </td>
                                            <td className="distribution-cell">
                                                <div className="distribution-bar">
                                                    <div 
                                                        className="home-bar" 
                                                        style={{ width: `${homePercentage}%` }}
                                                    ></div>
                                                    <div 
                                                        className="away-bar" 
                                                        style={{ width: `${awayPercentage}%` }}
                                                    ></div>
                                                </div>
                                            </td>
                                            <td className="count-cell">
                                                {awayCount}
                                                <span className="percentage">({awayPercentage}%)</span>
                                            </td>
                                            <td className="total-cell">{totalCount}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default TeamEventComparison;